function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import Downshift from "downshift";
import { Flex } from "sprout-ui-primitives";
import styled from "styled-components";
import { themeGet, media } from "sprout-ui-theme";
import { ScrollContainer } from "sprout-ui-dom/dist/ScrollContainer";
import { debounce } from "lodash";
import { useLocation, useHistory } from "react-router-dom";
import { stringify } from "query-string";
import clsx from "clsx";
import { useAfterMountEffect } from "sprout-hooks";
import { useActionRegistryGlobalHandler } from "@sprout/list-navigation";
import { SearchProvider } from "./SearchContext";
import SearchMenu from "./Search_Menu";
import SearchClear from "./Search_Clear";
import { SearchScope, SearchVariant } from "./types";
import SearchInput from "./Search_Input";
import SearchIcon from "./Search_Icon";
import SearchDropdown from "./Search_Dropdown";
import useSearchPageQueryString from "./useSearchPageQueryString";
import useSearchPageParams from "./useSearchPageParams";
const SearchWrapper = styled(Flex)`
  position: relative;

  &.Search--focus {
    &::after {
      position: absolute;
      content: "";
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border: 3px solid #7fc1ff;
      border-radius: inherit;
      pointer-events: none;
    }
  }

  button {
    &:last-of-type {
      margin-right: 8px;
    }
  }
`;
const StyledSearchWrapper = styled(SearchWrapper)`
  position: relative;
`;
const SearchDropdownWrapper = styled(ScrollContainer)`
  background: ${themeGet("colors.white.100")};

  .Search--focus & {
    position: fixed;
    top: 56px;
    left: 0;
    right: 0;
    bottom: 0;

    ${media.up("medium", "pointer")} {
      left: 50%;
      right: auto;
      bottom: auto;

      transform: translateX(-50%);
      width: calc(100% - 128px);
      max-width: 1200px;

      box-shadow: 0 1px 20px ${themeGet("colors.black.10")};
      border-radius: 30px;
    }
  }
`;
const itemToString = ()=>"";
function getSearchPageViewsGlobalSearchScope(view) {
    if (view === "people") return SearchScope.people;
    else if (view === "sprout") return SearchScope.sprout;
    else return SearchScope["my-trees"];
}
const Search = (props)=>{
    const { variant , onFocus , onBlur  } = props;
    const { q , f  } = useSearchPageQueryString();
    const history = useHistory();
    const { view , topicId  } = useSearchPageParams();
    const isSearchPage = view && [
        "my-trees",
        "sprout",
        "people"
    ].includes(view);
    const isSearchPageTopic = view && [
        "daily",
        "read-later"
    ].includes(view) || !!topicId;
    const { pathname , state  } = useLocation();
    const initialSearchScope = useMemo(()=>{
        return getSearchPageViewsGlobalSearchScope(view);
    }, [
        view
    ]);
    const [searchScope, setSearchScope] = useState(initialSearchScope);
    useAfterMountEffect(()=>{
        return setSearchScope(getSearchPageViewsGlobalSearchScope(view));
    }, [
        view
    ]);
    //
    /**
   * When transitioning from `TopicPage` this state is populated. Therefore
   * focus input.
   *
   * We also replace the current route with a one without state to handle
   * the case where the user navigates away from `SearchDropdown` and navigates
   * back to `SearchPage` `Views` `Topic`.
   */ const { focusSearchInput  } = state || {};
    const inputRef = useRef(null);
    useEffect(()=>{
        var ref;
        if (focusSearchInput) (ref = inputRef.current) === null || ref === void 0 ? void 0 : ref.focus();
    }, [
        focusSearchInput
    ]);
    //
    const [inputValue, setInputValue] = useState(q);
    const inputValueRef = useRef(inputValue);
    const [searchTerm, setSearchTerm] = useState(inputValue);
    const debouncedSetSearchTerm = useRef(debounce((nextValue)=>{
        setSearchTerm(nextValue);
    }, 500));
    const handleInputValueChange = useCallback((nextInputValue)=>{
        setInputValue(nextInputValue);
        debouncedSetSearchTerm.current(nextInputValue);
        inputValueRef.current = nextInputValue;
    }, []);
    //
    /**
   * 1. Update input if the query string changes, due to route changes.
   * i.e. going backwards and forwards between `SearchPage`.
   *
   * 2. Restore the input if the pathname changes.
   * i.e. on `SearchPage` `Views` `Global`, delete input, and
   * change `searchScope` via `SearchMenu`.
   */ useEffect(()=>{
        if (q !== inputValueRef.current) handleInputValueChange(q);
    }, [
        q,
        pathname,
        handleInputValueChange
    ]);
    /**
   * 3. Restore `Search` back to default when navigating away from `SearchPage`.
   * Only needed on desktop variant.
   */ useAfterMountEffect(()=>{
        if (variant === SearchVariant.DESKTOP) {
            if (!isSearchPage && !isSearchPageTopic) {
                setSearchScope(SearchScope["my-trees"]);
                handleInputValueChange("");
            }
        }
    }, [
        handleInputValueChange,
        isSearchPage,
        isSearchPageTopic,
        variant
    ]);
    //
    const stateReducer = useCallback((downshiftState, changes)=>{
        switch(changes.type){
            case Downshift.stateChangeTypes.blurInput:
            case Downshift.stateChangeTypes.mouseUp:
            case Downshift.stateChangeTypes.touchEnd:
                /**
         * `touchEnd` is needed mobile to preserve the input text on blur.
         */ return _objectSpread({}, changes, {
                    inputValue: downshiftState.inputValue
                });
            case Downshift.stateChangeTypes.keyDownEscape:
                return _objectSpread({}, changes, {
                    inputValue: downshiftState.inputValue
                });
            default:
                return changes;
        }
    }, []);
    //
    const handleChange = useCallback((selectedItem, stateAndHelpers)=>{
        if (selectedItem) {
            const { url  } = selectedItem;
            if (pathname !== url) history.push(url);
            if (inputRef.current) inputRef.current.blur();
            stateAndHelpers.clearSelection();
            // revert `searchScope` to default
            if (!isSearchPage) setSearchScope(SearchScope["my-trees"]);
        }
    }, [
        pathname,
        history,
        isSearchPage
    ]);
    useActionRegistryGlobalHandler("SEARCH", useCallback((_activeItems, event)=>{
        if (inputRef.current) {
            /**
         * Required in Chrome, prevent the character `/` from filling the input
         */ event === null || event === void 0 ? void 0 : event.preventDefault();
            inputRef.current.focus();
            return true;
        }
        return false;
    }, []), {
        skip: variant !== SearchVariant.DESKTOP
    });
    const search = stringify({
        q: q || undefined
    });
    useActionRegistryGlobalHandler("SEARCH_MY_TOPICS", useCallback(()=>{
        const nextSearchScope = SearchScope["my-trees"];
        if (isSearchPage) {
            history.push({
                pathname: `/search/${nextSearchScope}`,
                search
            });
            return true;
        }
        setSearchScope(nextSearchScope);
        return true;
    }, [
        isSearchPage,
        history,
        search
    ]), {
        skip: isSearchPageTopic || variant === SearchVariant.MOBILE
    });
    useActionRegistryGlobalHandler("SEARCH_SPROUT", useCallback(()=>{
        const nextSearchScope = SearchScope.sprout;
        if (isSearchPage) {
            history.push({
                pathname: `/search/${nextSearchScope}`,
                search
            });
            return true;
        } else {
            setSearchScope(nextSearchScope);
            return true;
        }
    }, [
        isSearchPage,
        history,
        search
    ]));
    useActionRegistryGlobalHandler("SEARCH_PEOPLE", useCallback(()=>{
        const nextSearchScope = SearchScope.people;
        if (isSearchPage) {
            history.push({
                pathname: `/search/${nextSearchScope}`,
                search
            });
            return true;
        } else {
            setSearchScope(nextSearchScope);
            return true;
        }
    }, [
        isSearchPage,
        history,
        search
    ]));
    const handleEnter = useCallback(()=>{
        let nextPathname = pathname;
        if (!isSearchPage && !isSearchPageTopic) nextPathname = `/search/${searchScope}`;
        history.push({
            pathname: nextPathname,
            search: stringify({
                q: inputValueRef.current,
                /**
         * on `SearchPage` preserve filters that have been
         * selected
         */ f: f.length !== 0 ? f.join(",") : undefined
            })
        });
    }, [
        f,
        history,
        isSearchPage,
        isSearchPageTopic,
        pathname,
        searchScope
    ]);
    const searchProviderValue = useMemo(()=>({
            searchScope,
            setSearchScope
        }), [
        searchScope
    ]);
    return /*#__PURE__*/ _jsx(SearchProvider, {
        value: searchProviderValue,
        children: /*#__PURE__*/ _jsx(Downshift, {
            itemToString: itemToString,
            inputValue: inputValue,
            onInputValueChange: handleInputValueChange,
            stateReducer: stateReducer,
            onChange: handleChange,
            children: ({ getInputProps , getMenuProps , getRootProps , clearSelection , isOpen , openMenu , closeMenu , highlightedIndex , getItemProps ,  })=>{
                const className = clsx({
                    "Search--focus": isOpen
                });
                return /*#__PURE__*/ _jsxs(StyledSearchWrapper, _objectSpread({
                    alignItems: "center",
                    borderRadius: "30px",
                    bg: "white.100",
                    height: "36px",
                    className: className
                }, getRootProps(), {
                    children: [
                        /*#__PURE__*/ _jsx(SearchIcon, {}),
                        /*#__PURE__*/ _jsx(SearchInput, _objectSpread({
                            ref: inputRef,
                            placeholder: "Search"
                        }, getInputProps({
                            onFocus () {
                                openMenu();
                                if (onFocus) onFocus();
                            },
                            onBlur () {
                                /**
                     * Because of how the `SearchInput` is in a `createPortal`,
                     * it causes focus on mount issues if we try to remove the
                     * location state immediately after mount.
                     *
                     * So the trick is to remove the location state on blur instead.
                     * */ if (focusSearchInput) history.replace({
                                    state: undefined
                                });
                                closeMenu();
                                if (onBlur) onBlur();
                            },
                            onKeyDown (event) {
                                const target = event.target;
                                if (event.key === "Escape") {
                                    target.blur();
                                    return;
                                }
                                if (event.key === "Enter" && target.value.length !== 0 && highlightedIndex === null) {
                                    handleEnter();
                                    target.blur();
                                }
                            }
                        }), {
                            /**
                 * To appease TypeScript,
                 * https://github.com/downshift-js/downshift/issues/718
                 */ as: undefined
                        })),
                        /*#__PURE__*/ _jsx(SearchDropdownWrapper, _objectSpread({}, getMenuProps(), {
                            children: inputValue.length !== 0 && isOpen && /*#__PURE__*/ _jsx(SearchDropdown, {
                                searchTerm: searchTerm,
                                getItemProps: getItemProps
                            })
                        })),
                        inputValue.length !== 0 && /*#__PURE__*/ _jsx(SearchClear, {
                            onClick: ()=>clearSelection()
                        }),
                        !isSearchPageTopic && variant === SearchVariant.DESKTOP && /**
                 * Hide `SearchMenu` on `SearchPage` `Views` `Topic`
                 */ /*#__PURE__*/ _jsx(SearchMenu, {})
                    ]
                }));
            }
        })
    });
};
export default Search;
